import { useEffect, useRef, useState } from 'react'
import Filmstrip from '../Filmstrip'
import Confetti from '../Confetti'
import {
  IconArrowRight, IconTrophy, IconCrown, IconTicket, IconStar, IconReplay,
} from '../icons'

/**
 * H2H results screen.
 *   forHost=true  — winner announcement + all team chains side by side
 *                   + Play Again / Back to Lobby / End Party actions
 *   forHost=false — personal result (your team won or lost) + your team's chain
 *                   + waiting-for-host pulse. Refund fires for zero-move players.
 */
export default function PartyH2HDone({
  party,
  currentUserId,
  forHost,
  onPlayAgain,
  onBackToLobby,
  onEndParty,
  onRefundMyTicket,
}) {
  const players  = party?.players ?? {}
  const teams    = Array.isArray(party?.teams) ? party.teams : []
  const h2hChains = party?.h2hChains ?? {}
  const playerMoves = party?.playerMoves ?? {}
  const startActor  = party?.startActor
  const goalActor   = party?.goalActor
  const cameoLastBy = party?.cameoLastBy
  const winnerId    = party?.h2hWinner

  const winningTeam = teams.find((t) => t.id === winnerId)
  const myTeam = teams.find((t) => (t.playerIds || []).includes(currentUserId))
  const iWon   = myTeam && winnerId === myTeam.id

  // Hop counts per team
  const hopsByTeam = {}
  for (const t of teams) {
    const chain = h2hChains[t.id] || []
    hopsByTeam[t.id] = chain.filter((c) => c.type === 'movie').length
  }

  // ── Refund: zero moves = ticket back ──────────────────────────────────────
  // Refund toast message is plain text (the toast surface adds its own visual
  // chrome). Emoji previously baked in here has been dropped.
  const refundedRef = useRef(false)
  useEffect(() => {
    if (refundedRef.current) return
    if (forHost) return
    if (!currentUserId || !onRefundMyTicket) return
    if (party?.status !== 'h2h-results') return
    const myMoves = playerMoves[currentUserId] || 0
    if (myMoves > 0) return
    refundedRef.current = true
    onRefundMyTicket("You didn't get to make a move — ticket refunded.")
  }, [forHost, currentUserId, onRefundMyTicket, playerMoves, party?.status])

  // ── Celebration on solve ──────────────────────────────────────────────────
  const [celebrating, setCelebrating] = useState(false)
  useEffect(() => {
    if (!winningTeam) return
    setCelebrating(true)
    const id = setTimeout(() => setCelebrating(false), 4500)
    return () => clearTimeout(id)
  }, [winningTeam])

  const Matchup = () => (
    <p className="h2h-done__matchup">
      <strong>{startActor?.name}</strong>
      <span className="h2h-done__matchup-arrow" aria-hidden="true">
        <IconArrowRight size={16} />
      </span>
      <strong>{goalActor?.name}</strong>
    </p>
  )

  // ── HOST view ─────────────────────────────────────────────────────────────
  if (forHost) {
    // Order teams by hops asc (winner first), DNF teams (no chain) at end
    const teamsSorted = [...teams].sort((a, b) => {
      if (a.id === winnerId) return -1
      if (b.id === winnerId) return 1
      return (hopsByTeam[a.id] || 999) - (hopsByTeam[b.id] || 999)
    })
    return (
      <div className="h2h-done h2h-done--host">
        {celebrating && <Confetti />}

        {winningTeam ? (
          <div className="h2h-done__spotlight">
            <div className="h2h-done__spotlight-icon" aria-hidden="true">
              <IconTrophy size={32} />
            </div>
            <h2 className="h2h-done__title">{winningTeam.name} takes it!</h2>
          </div>
        ) : (
          <h2 className="h2h-done__title h2h-done__title--no-winner">No winner</h2>
        )}

        <Matchup />

        {cameoLastBy && (
          <p className="h2h-done__cameo-note">
            <IconStar size={14} aria-hidden="true" />
            <span>{cameoLastBy} called in a Cameo!</span>
          </p>
        )}

        <div className="h2h-done__teams">
          {teamsSorted.map((t) => {
            const chain = h2hChains[t.id] || []
            const isWinner = t.id === winnerId
            return (
              <div
                key={t.id}
                className={`h2h-done__team${isWinner ? ' h2h-done__team--winner' : ''}`}
              >
                <div className="h2h-done__team-header">
                  <span className="h2h-done__team-name">
                    {isWinner && (
                      <span className="h2h-done__team-crown" aria-label="Winner">
                        <IconCrown size={14} />
                      </span>
                    )}
                    <span>{t.name}</span>
                  </span>
                  <span className="h2h-done__team-hops">
                    {hopsByTeam[t.id]} hop{hopsByTeam[t.id] !== 1 ? 's' : ''}
                  </span>
                </div>
                <div className="h2h-done__team-chain">
                  {chain.length > 0
                    ? <Filmstrip chain={chain} onItemClick={() => {}} hidePlaceholder />
                    : <p className="h2h-done__team-empty">— never got going</p>}
                </div>
                <div className="h2h-done__team-roster">
                  {(t.playerIds || []).map((uid) => {
                    const p = players[uid]
                    const moves = playerMoves[uid] || 0
                    const initial = (p?.username || 'G').charAt(0).toUpperCase()
                    return (
                      <div key={uid} className="h2h-done__roster-row">
                        <div className="h2h-done__roster-avatar">
                          {p?.avatar
                            ? <img src={p.avatar} alt="" />
                            : <span className="party-avatar__initial">{initial}</span>}
                        </div>
                        <span className="h2h-done__roster-name">{p?.username || 'Guest'}</span>
                        <span className="h2h-done__roster-moves">
                          {moves} move{moves !== 1 ? 's' : ''}
                        </span>
                        {moves === 0 && (
                          <span className="party-refund-badge" title="Ticket refunded">
                            <IconTicket size={10} aria-hidden="true" />
                            <span>+1</span>
                          </span>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="h2h-done__actions">
          {onPlayAgain && (
            <button className="pb-action-btn pb-action-btn--primary" onClick={onPlayAgain}>
              <IconReplay size={18} aria-hidden="true" />
              <span>Play Again</span>
            </button>
          )}
          {onBackToLobby && (
            <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby}>
              Back to Lobby
            </button>
          )}
          {onEndParty && (
            <button className="pb-action-btn pb-action-btn--muted" onClick={onEndParty}>
              End Party
            </button>
          )}
        </div>
      </div>
    )
  }

  // ── PLAYER view ───────────────────────────────────────────────────────────
  const myChain = myTeam ? (h2hChains[myTeam.id] || []) : []
  const myMoves = playerMoves[currentUserId] || 0
  return (
    <div className="h2h-done">
      <h2
        className="h2h-done__title"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
      >
        {iWon ? (
          <>
            <IconTrophy size={24} aria-hidden="true" />
            <span>You Won!</span>
          </>
        ) : winningTeam
          ? `${winningTeam.name} wins`
          : 'Game over'}
      </h2>
      <Matchup />

      {myTeam && (
        <>
          <p className="h2h-done__my-team-label">
            You're on <strong>{myTeam.name}</strong> · {hopsByTeam[myTeam.id]} hops
          </p>
          {myChain.length > 0 && (
            <div className="h2h-done__chain">
              <Filmstrip chain={myChain} onItemClick={() => {}} hidePlaceholder />
            </div>
          )}
        </>
      )}

      <p className="h2h-done__my-contrib">
        You contributed <strong>{myMoves}</strong> move{myMoves !== 1 ? 's' : ''}
        {myMoves === 0 && (
          <span className="party-refund-badge" title="Ticket refunded" style={{ marginLeft: 8 }}>
            <IconTicket size={10} aria-hidden="true" />
            <span>+1</span>
          </span>
        )}
      </p>

      <p className="h2h-done__waiting">Waiting for host...</p>

      <button className="pb-action-btn pb-action-btn--outline" onClick={onBackToLobby} style={{ marginTop: 16 }}>
        Back to Lobby
      </button>
    </div>
  )
}
